import React, { useState, useEffect } from 'react';
import Loader from '../components/Loader';

const AboutPage = () => {
  const [activeChef, setActiveChef] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.scroll-reveal');
    sections.forEach(section => observer.observe(section));

    // Simulate loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  const chefs = [
    {
      id: 1,
      name: "Folafunmi Adebowale",
      role: "Founder",
      avatar: "👩🏿‍🍳",
      bio: "Born and raised in Lagos, Chef Folafunmi learned the art of Nigerian cuisine from his grandmother. With over 15 years of experience, she brings authentic flavors from her family's kitchen to yours.",
      specialty: "Jollof Rice & Traditional Soups",
      experience: "15+ Years",
      background: "Graduated from Lagos Culinary Institute, worked in top Nigerian restaurants before founding TiwaTinwa in 2020.",
      quote: "Every dish tells a story of our rich Nigerian heritage. I cook with my heart and soul."
    },
    {
      id: 2,
      name: "Chef Emeka Okafor",
      role: "Sous Chef & Grill Master",
      avatar: "👨🏿‍🍳",
      bio: "From Enugu to your table, Chef Emeka specializes in perfectly grilled meats and authentic pepper soups. His secret? Family recipes passed down for generations.",
      specialty: "Suya & Grilled Meats",
      experience: "12+ Years",
      background: "Trained in traditional Igbo cooking methods, specialized in meat preparation and spice blending.",
      quote: "The perfect suya is an art form - it's all about the spice blend and timing!"
    },
    {
      id: 3,
      name: "Chef Abisinuola Ololade",
      role: "Pastry Chef & Snack Expert",
      avatar: "👩🏿‍🦱",
      bio: "Specializing in Nigerian snacks and pastries, Chef Abisinuola creates the most authentic meat pies, puff puff, and chin chin in Lagos. Her techniques blend tradition with modern innovation.",
      specialty: "Nigerian Pastries & Snacks",
      experience: "10+ Years",
      background: "Self-taught baker who perfected family recipes, known for the crispiest meat pies in Lagos.",
      quote: "Nigerian snacks should taste like childhood memories - warm, comforting, and full of love."
    }
  ];

  const achievements = [
    {
      icon: "🏆",
      title: "Best Nigerian Restaurant 2023",
      description: "Lagos Food Awards Winner"
    },
    {
      icon: "⭐",
      title: "4.8/5 Customer Rating",
      description: "Based on 2,500+ reviews"
    },
    {
      icon: "🚚",
      title: "10,000+ Orders Delivered",
      description: "Happy customers across Lagos"
    },
    {
      icon: "👨‍👩‍👧‍👦",
      title: "Family Owned & Operated",
      description: "Three generations of recipes"
    }
  ];

  const values = [
    {
      icon: "🇳🇬",
      title: "Authentic Nigerian Flavors",
      description: "We use traditional recipes and cooking methods passed down through generations, ensuring every bite tastes like home."
    },
    {
      icon: "🌱",
      title: "Fresh Local Ingredients",
      description: "We source the freshest ingredients from local Nigerian markets, supporting our community while delivering quality."
    },
    {
      icon: "❤️",
      title: "Cooked with Love",
      description: "Every dish is prepared with passion and care, just like our grandmothers taught us - with love in every spoon."
    },
    {
      icon: "⚡",
      title: "Fast & Reliable Delivery",
      description: "Hot, fresh Nigerian food delivered to your doorstep quickly and safely, so you can enjoy authentic flavors anytime."
    }
  ];

  if (loading) return <Loader text="Loading menu..." />;

  return (
    <div className="main-container about-page">
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
        color: 'white',
        padding: '5rem 0',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `
            radial-gradient(circle at 25% 25%, white 3px, transparent 3px),
            radial-gradient(circle at 75% 75%, white 2px, transparent 2px)
          `,
          backgroundSize: '60px 60px, 40px 40px',
          animation: 'drift 25s linear infinite'
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1rem',
            animation: 'bounce 2s infinite'
          }}>
            🇳🇬
          </div>
          <h1 style={{
            fontSize: '4rem',
            marginBottom: '1.5rem',
            textShadow: '3px 3px 6px rgba(0,0,0,0.3)',
            fontWeight: '900'
          }}>
            About TiwaTinwa
          </h1>
          <p style={{
            fontSize: '1.5rem',
            marginBottom: '2rem',
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
            maxWidth: '800px',
            margin: '0 auto 2rem'
          }}>
            Bringing authentic Nigerian flavors to your doorstep since 2020. 
            Where tradition meets convenience, and every meal tells a story.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '2rem',
            flexWrap: 'wrap',
            marginTop: '3rem'
          }}>
            {achievements.map((achievement, index) => (
              <div key={index} style={{
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                padding: '1.5rem',
                borderRadius: '20px',
                textAlign: 'center',
                minWidth: '200px',
                border: '2px solid rgba(255,255,255,0.2)',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>
                  {achievement.icon}
                </div>
                <h3 style={{
                  fontSize: '1.1rem',
                  marginBottom: '0.5rem',
                  fontWeight: '700'
                }}>
                  {achievement.title}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  opacity: 0.9
                }}>
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section">
        <div className="container">
          <div className="about-story-grid" style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4rem',
            alignItems: 'center',
            marginBottom: '4rem'
          }}>
            <div>
              <h2 style={{
                fontSize: '3rem',
                color: 'var(--nigeria-green)',
                marginBottom: '2rem',
                fontWeight: '800'
              }}>
                🏠 Our Story
              </h2>
              <div style={{ fontSize: '1.2rem', lineHeight: '1.8', color: 'var(--text-dark)' }}>
                <p style={{ marginBottom: '1.5rem' }}>
                  <strong>TiwaTinwa</strong> began in 2020 when Chef Folafunmi Adebowale realized that busy Lagosians were missing out on authentic, home-cooked Nigerian meals. Armed with her grandmother's recipe book and a dream to preserve Nigerian culinary traditions, she started cooking from her kitchen.
                </p>
                <p style={{ marginBottom: '1.5rem' }}>
                  What started as delivering meals to friends and neighbors quickly grew into something special. Word spread about the "chef who cooks like your grandmother" - and TiwaTinwa was born.
                </p>
                <p style={{ marginBottom: '1.5rem' }}>
                  Today, we're proud to serve over <strong>10,000 customers</strong> across Lagos, bringing the authentic taste of Nigerian cuisine to busy professionals, homesick students, and food lovers who crave the real thing.
                </p>
                <div style={{
                  background: 'rgba(0,135,81,0.1)',
                  padding: '1.5rem',
                  borderRadius: '15px',
                  borderLeft: '5px solid var(--nigeria-green)',
                  fontStyle: 'italic',
                  marginTop: '2rem'
                }}>
                  <strong>"Every dish we serve carries the soul of Nigeria - the warmth of our culture, the richness of our traditions, and the love of our people."</strong>
                  <br />
                  <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                    - Chef Folafunmi Adebowale, Founder
                  </span>
                </div>
              </div>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
              borderRadius: '25px',
              padding: '3rem',
              color: 'white',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                backgroundImage: 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'60\' height=\'60\' viewBox=\'0 0 60 60\'><circle cx=\'30\' cy=\'30\' r=\'2\' fill=\'white\'/></svg>")',
                animation: 'drift 20s linear infinite'
              }}></div>
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🍲</div>
                <h3 style={{
                  fontSize: '2rem',
                  marginBottom: '1rem',
                  fontWeight: '700'
                }}>
                  Our Mission
                </h3>
                <p style={{
                  fontSize: '1.2rem',
                  lineHeight: '1.6',
                  marginBottom: '2rem'
                }}>
                  To preserve and share the authentic flavors of Nigerian cuisine, making traditional home-cooked meals accessible to everyone, everywhere.
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                  marginTop: '2rem'
                }}>
                  <div>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎯</div>
                    <div style={{ fontSize: '0.9rem' }}>Authentic Recipes</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🌟</div>
                    <div style={{ fontSize: '0.9rem' }}>Quality Ingredients</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🚀</div>
                    <div style={{ fontSize: '0.9rem' }}>Fast Delivery</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>❤️</div>
                    <div style={{ fontSize: '0.9rem' }}>Made with Love</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Chefs Section */}
      <section style={{
        background: '#f8f9fa',
        padding: '5rem 0'
      }}>
        <div className="container">
          <h2 style={{
            fontSize: '3rem',
            color: 'var(--nigeria-green)',
            textAlign: 'center',
            marginBottom: '3rem',
            fontWeight: '800'
          }}>
            👨‍🍳 Meet Our Master Chefs
          </h2>
          <div className="about-chefs-grid">
            {/* Chef Selection Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {chefs.map((chef, index) => (
                <button
                  key={chef.id}
                  onClick={() => setActiveChef(index)}
                  style={{
                    background: activeChef === index
                      ? 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))'
                      : 'white',
                    color: activeChef === index ? 'white' : 'var(--text-dark)',
                    border: '2px solid var(--nigeria-green)',
                    borderRadius: '20px',
                    padding: '1.5rem',
                    marginBottom: '1rem', // <-- add this line
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: '600'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <div style={{ fontSize: '2.5rem' }}>
                      {chef.avatar}
                    </div>
                    <div>
                      <h4 style={{
                        margin: 0,
                        fontSize: '1.1rem',
                        marginBottom: '0.3rem'
                      }}>
                        {chef.name}
                      </h4>
                      <p style={{
                        margin: 0,
                        fontSize: '0.9rem',
                        opacity: 0.8
                      }}>
                        {chef.role}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {/* Active Chef Details */}
            <div className="chef-details-section" style={{
              background: 'white',
              borderRadius: '25px',
              boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
              border: '2px solid #f0f0f0'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '2rem',
                marginBottom: '2rem'
              }}>
                <div style={{
                  fontSize: '5rem',
                  background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
                  borderRadius: '50%',
                  width: '120px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {chefs[activeChef].avatar}
                </div>
                <div>
                  <h3 style={{
                    fontSize: '2.5rem',
                    color: 'var(--nigeria-green)',
                    marginBottom: '0.5rem',
                    fontWeight: '800'
                  }}>
                    {chefs[activeChef].name}
                  </h3>
                  <p style={{
                    fontSize: '1.3rem',
                    color: 'var(--warm-orange)',
                    fontWeight: '600',
                    marginBottom: '1rem'
                  }}>
                    {chefs[activeChef].role}
                  </p>
                  <div style={{
                    display: 'flex',
                    gap: '2rem',
                    fontSize: '0.9rem',
                    color: 'var(--text-light)'
                  }}>
                    <div>
                      <strong>Experience:</strong> {chefs[activeChef].experience}
                    </div>
                    <div>
                      <strong>Specialty:</strong> {chefs[activeChef].specialty}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{
                background: 'rgba(0,135,81,0.05)',
                padding: '2rem',
                borderRadius: '15px',
                marginBottom: '2rem',
                borderLeft: '5px solid var(--nigeria-green)'
              }}>
                <p style={{
                  fontSize: '1.2rem',
                  fontStyle: 'italic',
                  color: 'var(--text-dark)',
                  marginBottom: '1rem',
                  lineHeight: '1.6'
                }}>
                  "{chefs[activeChef].quote}"
                </p>
              </div>
              <p style={{
                fontSize: '1.1rem',
                lineHeight: '1.7',
                color: 'var(--text-dark)',
                marginBottom: '1.5rem'
              }}>
                {chefs[activeChef].bio}
              </p>
              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '15px'
              }}>
                <h4 style={{
                  fontSize: '1.2rem',
                  color: 'var(--nigeria-green)',
                  marginBottom: '1rem',
                  fontWeight: '700'
                }}>
                  🎓 Background
                </h4>
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  color: 'var(--text-dark)',
                  margin: 0
                }}>
                  {chefs[activeChef].background}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="section">
        <div className="container">
          <h2 style={{
            fontSize: '3rem',
            color: 'var(--nigeria-green)',
            textAlign: 'center',
            marginBottom: '3rem',
            fontWeight: '800'
          }}>
            💎 Our Core Values
          </h2>
          <div className="about-values-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '2rem'
          }}>
            {values.map((value, index) => (
              <div key={index} style={{
                background: 'white',
                borderRadius: '25px',
                padding: '2.5rem',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                border: '2px solid #f0f0f0',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>
                  {value.icon}
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  color: 'var(--nigeria-green)',
                  marginBottom: '1rem',
                  fontWeight: '700'
                }}>
                  {value.title}
                </h3>
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  color: 'var(--text-dark)'
                }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section style={{
        background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
        color: 'white',
        padding: '5rem 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{
            fontSize: '3rem',
            marginBottom: '1.5rem',
            fontWeight: '800'
          }}>
            🤝 Join the TiwaTinwa Family
          </h2>
          <p style={{
            fontSize: '1.3rem',
            marginBottom: '3rem',
            maxWidth: '600px',
            margin: '0 auto 3rem'
          }}>
            Experience authentic Nigerian cuisine prepared with love, tradition, and the finest ingredients. 
            Your taste buds will thank you!
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '2rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.href = '/menu'}
              style={{
                background: 'white',
                color: 'var(--nigeria-green)',
                border: 'none',
                padding: '15px 30px',
                borderRadius: '25px',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              🍽️ Order Now
            </button>
            <button
              onClick={() => window.location.href = '/reviews'}
              style={{
                background: 'transparent',
                color: 'white',
                border: '2px solid white',
                padding: '15px 30px',
                borderRadius: '25px',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = 'var(--nigeria-green)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'white';
              }}
            >
              ⭐ Read Reviews
            </button>
            <button
              onClick={() => window.location.href = '/contact'}
              style={{
                background: 'transparent',
                color: 'white',
                border: '2px solid white',
                padding: '15px 30px',
                borderRadius: '25px',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = 'var(--nigeria-green)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'white';
              }}
            >
              📞 Get in Touch
            </button>
          </div>
        </div>
      </section>
      {/* Responsive styles for AboutPage */}
      <style>{`
        .main-container.about-page,
        .main-container.about-page section,
        .main-container.about-page .container {
          box-sizing: border-box;
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
        }
        .about-story-grid {
          width: 100%;
        }
        .about-chefs-grid {
          width: 100%;
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 2rem;
          align-items: start;
        }
        .chef-details-section {
          padding: 3rem;
        }
        @media (max-width: 600px) {
          .chef-details-section {
            padding: 1rem !important;
          }
          .chef-details-section h3 {
            font-size: 1.2rem !important;
          }
          .chef-details-section p,
          .chef-details-section div {
            font-size: 0.95rem !important;
          }
        }
        .about-values-grid {
          width: 100%;
        }
        @media (max-width: 1024px) {
          .main-container.about-page h1 {
            font-size: 2.2rem !important;
          }
          .main-container.about-page h2 {
            font-size: 1.5rem !important;
          }
        }
        @media (max-width: 900px) {
          .about-chefs-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
        }
        @media (max-width: 600px) {
          .main-container.about-page .container {
            padding: 0 0.5rem !important;
          }
          .main-container.about-page .section {
            padding: 1rem 0 !important;
          }
          .about-story-grid,
          .about-chefs-grid,
          .about-values-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .about-chefs-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .chef-details-section {
            padding: 1rem !important;
          }
          .chef-details-section h3 {
            font-size: 1.2rem !important;
          }
          .chef-details-section p,
          .chef-details-section div {
            font-size: 0.95rem !important;
          }
          .chef-details-section div[style*="font-size: 5rem"] {
            font-size: 2.5rem !important;
            width: 60px !important;
            height: 60px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutPage;